import {
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { createPortal } from "react-dom";

interface DropdownSurfaceProps {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  surfaceRef: RefObject<HTMLDivElement | null>;
  align?: "left" | "right";
  minWidth?: number;
  matchAnchorWidth?: boolean;
  className?: string;
  children: ReactNode;
}

const VIEWPORT_PAD = 8;
const CLOSE_MS = 150;

export function DropdownSurface({
  open,
  anchorRef,
  surfaceRef,
  align = "left",
  minWidth = 132,
  matchAnchorWidth = true,
  className = "",
  children,
}: DropdownSurfaceProps) {
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  const [style, setStyle] = useState<CSSProperties | null>(null);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      return;
    }

    if (!mounted) return;
    setClosing(true);
    const timer = window.setTimeout(() => {
      setMounted(false);
      setClosing(false);
    }, CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [mounted, open]);

  useLayoutEffect(() => {
    if (!mounted) return;

    const update = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      const menuWidth = matchAnchorWidth
        ? Math.max(minWidth, rect.width)
        : minWidth;
      const preferredLeft =
        align === "right" ? rect.right - menuWidth : rect.left;
      const maxLeft = window.innerWidth - menuWidth - VIEWPORT_PAD;
      const left = Math.max(VIEWPORT_PAD, Math.min(preferredLeft, maxLeft));

      setStyle({
        left,
        minWidth: menuWidth,
        top: rect.bottom + 6,
      });
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [align, anchorRef, matchAnchorWidth, minWidth, mounted]);

  if (!mounted || typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={surfaceRef}
      aria-hidden={!open}
      className={`fintheon-dropdown-surface t-dropdown fixed z-[9999] py-1 ${
        open && !closing ? "is-open" : ""
      } ${closing ? "is-closing" : ""} ${className}`}
      data-origin={align === "right" ? "top-right" : "top-left"}
      style={style ?? undefined}
    >
      {children}
    </div>,
    document.body,
  );
}
